import React from "react"
import {
  dateAndTime,
  EquifaxPrequalReport,
  EqDates,
  formatFraudAlertContactPhone,
  FraudAlertContactPhone,
  getUniqueAlertContactPhones,
} from "@oneethos/shared"
import { FiAlertTriangle } from "react-icons/fi"
import { MdOutlinePhone } from "react-icons/md"

type CreditReportHeaderProps = {
  item: any
  report: EquifaxPrequalReport
}

type FraudAlertPhoneListProps = {
  phones: FraudAlertContactPhone[]
}

const FraudAlertPhoneList = ({ phones }: FraudAlertPhoneListProps) => {
  if (phones.length === 0) {
    return (
      <span className="d-flex align-items-center gap-2 fw-bold mt-3">
        <MdOutlinePhone size={20} className="text-danger me-1 fw-bold" />
        Not available
      </span>
    )
  }

  return (
    <>
      {phones.map((phone) => (
        <span
          key={phone.telephoneNumber}
          className="d-flex align-items-center gap-2 fw-bold mt-3"
        >
          <MdOutlinePhone size={20} className="text-danger me-1 fw-bold" />
          {formatFraudAlertContactPhone(phone)}
        </span>
      ))}
    </>
  )
}

export const CreditReportHeader = ({
  item,
  report
}: CreditReportHeaderProps) => {
  const dateOfBirthString = item?.request?.dateOfBirth
  const age = EqDates.calculateAge(dateOfBirthString)
  const isElderlyCustomer = age !== null && age >= 65
  const isFraudVictim = report.fraudVictimIndicator
  const alertContactPhones = getUniqueAlertContactPhones(report.alertContacts)

  return (
    <div className="mb-1">
      <div className="d-flex flex-row justify-content-between mb-3">
        <div className="d-flex align-items-center gap-4">
          <h5 className="m-0">
            Prequal: {item.customerName}
          </h5>
          <div className="d-flex align-items-center gap-3 border-start border-1 ps-2">
            <div className="ms-2">Age: {age !== null ? <span className="fw-bold">{age}</span> : 'N/A'}</div>
            {isElderlyCustomer &&
              <div className="alert alert-warning d-flex align-items-center gap-2 p-1 px-2 m-0">
                <FiAlertTriangle />
                <span>Elderly Customer</span>
              </div>}
          </div>
        </div>
        <div>
          <div className="help mb-1">{dateAndTime(new Date(item.createdDate))}</div>
        </div>
      </div>
      {item.comboName ? (
        <div className="help mb-3">
          Joint Application With: {item.comboName}
        </div>
      ) : null}
      {isFraudVictim ?
        <div className="rounded-4 p-3" style={{ backgroundColor: '#FDF2F0' }}>
          <div className="d-flex align-items-start">
            <div>
              <FiAlertTriangle size={20} className="text-danger me-2" />
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold text-danger fs-6 mb-2">
                Fraud Victim Indicator
              </div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge text-danger border border-danger-subtle p-2 rounded-1">
                  CODE: {report.fraudVictimIndicator.code}
                </span>
                <span className="badge text-danger border border-danger-subtle p-2 rounded-1">
                  {report.fraudVictimIndicator.description}
                </span>
              </div>
              <p className="text-danger mb-0">
                A fraud alert has been identified on this credit report. Bankers should
                follow enhanced identity verification procedures before extending
                credit. Contact the customer using a trusted phone number on file.
                <FraudAlertPhoneList phones={alertContactPhones} />
              </p>
            </div>
          </div>
        </div>
        : null}
    </div>
  )
}
